import styled from '@emotion/styled/macro';
import rem from '../../utils/rem/rem';
import { Loading } from '../Loading/Loading';
import { CaretPosition, StyledProps } from './Button.types';

export const PrimaryButton = styled.button<StyledProps>`
  background: ${({ theme }) => theme.colors.red};
  position: relative;
  border: none;
  border-radius: ${rem(8)};
  color: ${({ theme }) => theme.colors.white};
  font-family: 'Open Sans';
  font-size: ${({ isSmall }) => (isSmall ? rem(14) : rem(16))};
  font-weight: 600;
  letter-spacing: 1px;
  height: ${({ isSmall }) => (isSmall ? rem(32) : rem(40))};
  padding: ${rem(0)} ${rem(32)};
  text-transform: uppercase;
  display: ${({ isLoading }) => (isLoading ? 'flex' : 'block')};
  align-items: center;

  &:hover {
    box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.3);
  }

  &:active {
    background: ${({ theme }) => theme.colors.redPressed};
    border: none;
    box-shadow: inset 2px 3px 5px rgba(0, 0, 0, 0.5);
  }

  &:focus-visible {
    ${({ theme }) => theme.states.focus};
    outline-offset: 2px;
  }

  &:disabled {
    background: ${({ theme, isLoading }) => (!isLoading ? theme.colors.gray400 : '')};
    border: ${({ isLoading }) => (!isLoading ? 'none' : '')};
    box-shadow: ${({ isLoading }) => (!isLoading ? 'none' : '')};
    color: ${({ theme, isLoading }) => (!isLoading ? theme.colors.gray600 : '')};
    pointer-events: none;
  }

  ${Loading} {
    position: absolute;
    right: 12px;
  }
`;

export const PrimaryWordWrapButton = styled(PrimaryButton)`
  white-space: pre-line;
  height: ${() => rem(44)};
`;

export const SecondaryButton = styled(PrimaryButton)`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.red};
  color: ${({ theme }) => theme.colors.red};

  &:active {
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.redPressed};
    color: ${({ theme }) => theme.colors.redPressed};
  }

  &:disabled {
    background: ${({ theme, isLoading }) => (!isLoading ? theme.colors.white : '')};
    border: 1px solid ${({ theme, isLoading }) => (!isLoading ? theme.colors.gray400 : '')};
    color: ${({ theme, isLoading }) => (!isLoading ? theme.colors.gray600 : '')};
    pointer-events: none;
  }
`;

export const TertiaryButton = styled(PrimaryButton)`
  background: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.white};
  box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.3);
  color: ${({ theme }) => theme.colors.gray900};
  font-size: ${rem(14)};
  font-weight: 400;
  letter-spacing: 0px;
  text-transform: none;

  &:active {
    background-color: ${({ theme }) => theme.colors.white};
    border: 2px solid ${({ theme }) => theme.colors.gray400};
    box-shadow: inset 2px 3px 6px rgba(0, 0, 0, 0.3);
  }
`;

export const TextButton = styled.button<StyledProps>`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.blue};
  border: none;
  background: none;
  border-radius: ${rem(8)};
  display: flex;
  align-items: center;
  font-family: 'Open Sans';
  font-size: ${rem(14)};
  font-weight: 600;
  padding: ${({ caret }) => {
    switch (caret) {
      case CaretPosition.Left:
        return `${rem(4)} ${rem(8)} ${rem(4)} 0`;
      case CaretPosition.Right:
        return `${rem(4)} 0 ${rem(4)} ${rem(8)}`;
      default:
        return `${rem(4)} ${rem(8)}`;
    }
  }};

  &:hover {
    text-decoration: underline;
  }

  &:active {
    color: ${({ theme }) => theme.colors.bluePressed};
  }

  &:disabled {
    color: ${({ theme, isLoading }) => (!isLoading ? theme.colors.gray600 : '')};
    pointer-events: none;
  }

  &:focus-visible {
    ${({ theme }) => theme.states.focus};
  }

  ${Loading} {
    margin: 0 12px;
  }
`;
